import { useEffect, useState } from 'react';
import useSWR from 'swr';
import useStockOrdersModuleState from '../../../../state/useStockOrdersModuleState';
import ModuleFilters, { TgetFiltersList } from '../../../../components/ModuleFilters/ModuleFilters';
import { getStockOrderFilters } from '../../../../services/stockOrders';
import { ISortFilter, SORT_ORDER } from '../../../../types/global';

import './StockOrdersFilters.scss';

interface IFilterCountry {
  name: string;
  code: string;
  flag: string;
}

interface IFilterProvider {
  id: string;
  name: string;
  code: string;
}

interface IFilterRequester {
  id: string;
  name: string;
  code: string;
}

interface IStockOrderFilters {
  countries: IFilterCountry[];
  providers: IFilterProvider[];
  requesters: IFilterRequester[];
}

/**
 * Componente para manejar los filtros de órdenes de stock
 * Incluye filtros por bodega, proveedor, solicitante y ordenamiento
 */
const StockOrdersFilters = () => {
  const { setStockOrdersFilters, stockOrdersFilters, setStockOrdersSortFilter } = useStockOrdersModuleState();

  const [openWarehouseFilter, setOpenWarehouseFilter] = useState<boolean>(false);
  const [openProviderFilter, setOpenProviderFilter] = useState<boolean>(false);
  const [openRequesterFilter, setOpenRequesterFilter] = useState<boolean>(false);
  const [openSortFilter, setOpenSortFilter] = useState<boolean>(false);

  const stockOrdersFilterMapping = {
    Bodega: 'country',
    Proveedor: 'provider',
    Solicitante: 'requester',
    Ordenar: 'sort'
  };

  // Obtener datos de filtros
  const { data: filterData } = useSWR<IStockOrderFilters>('stockOrderFilters', getStockOrderFilters);

  // Preparar las opciones para los filtros
  const warehouseOptions = filterData?.countries?.map((country: IFilterCountry) => country.name) || [];
  const providerOptions = filterData?.providers?.map((provider: IFilterProvider) => provider.name) || [];
  const requesterOptions = filterData?.requesters?.map((requester: IFilterRequester) => requester.name) || [];
  const sortOptions = ['ID de la orden', 'Fecha estimada', 'Costo total', 'Cantidad de unidad'];

  const sortOptionsToValueMap = new Map<string, string>([
    ['ID de la orden', 'orderId'],
    ['Fecha estimada', 'estimatedDeliveryDate'],
    ['Costo total', 'totalValue'],
    ['Cantidad de unidad', 'units']
  ]);

  const warehouseOptionsToCodeMap = new Map<string, string>(
    filterData?.countries?.map((country: IFilterCountry) => [country.name, country.code]) || []
  );

  const providerOptionsToIdMap = new Map<string, string>(
    filterData?.providers?.map((provider: IFilterProvider) => [provider.name, provider.id]) || []
  );

  const requesterOptionsToIdMap = new Map<string, string>(
    filterData?.requesters?.map((requester: IFilterRequester) => [requester.name, requester.id]) || []
  );

  const getStockOrdersFiltersList: TgetFiltersList = (
    formModuleFilters,
    handleClickFilterOption,
    getFilterActiveStatus
  ) => [
    {
      title: 'Bodega',
      openFunction: setOpenWarehouseFilter,
      openStatus: openWarehouseFilter,
      rawSelectOptions: formModuleFilters('Bodega', warehouseOptions, warehouseOptionsToCodeMap),
      handleChangeSelectedOption: handleClickFilterOption('country', 'value'),
      isFilterActive: getFilterActiveStatus('country')
    },
    {
      title: 'Proveedor',
      openFunction: setOpenProviderFilter,
      openStatus: openProviderFilter,
      rawSelectOptions: formModuleFilters('Proveedor', providerOptions, providerOptionsToIdMap),
      handleChangeSelectedOption: handleClickFilterOption('provider', 'value'),
      isFilterActive: getFilterActiveStatus('provider')
    },
    {
      title: 'Solicitante',
      openFunction: setOpenRequesterFilter,
      openStatus: openRequesterFilter,
      rawSelectOptions: formModuleFilters('Solicitante', requesterOptions, requesterOptionsToIdMap),
      handleChangeSelectedOption: handleClickFilterOption('requester', 'value'),
      isFilterActive: getFilterActiveStatus('requester')
    },
    {
      title: 'Ordenar',
      openFunction: setOpenSortFilter,
      openStatus: openSortFilter,
      rawSelectOptions: formModuleFilters('Ordenar', sortOptions, sortOptionsToValueMap),
      handleChangeSelectedOption: handleClickFilterOption('sort', 'value'),
      isFilterActive: getFilterActiveStatus('sort')
    }
  ];

  const handleSortChange = (sortFilter: ISortFilter) => {
    setStockOrdersSortFilter(sortFilter);
  };

  useEffect(() => {
    if (!stockOrdersFilters.some(filter => filter.name === 'sort')) {
      setStockOrdersFilters([
        ...stockOrdersFilters,
        {
          name: 'sort',
          includes: 'in',
          values: ['orderId']
        }
      ]);
    
      setStockOrdersSortFilter({ value: SORT_ORDER.DESC });
    }
  }, [setStockOrdersFilters, setStockOrdersSortFilter, stockOrdersFilters]);

  return (
    <div className="stockOrdersFilters">
      <div className="filtersContainer">
        <div className="w-384">
          <ModuleFilters
            moduleFilters={stockOrdersFilters}
            setModuleFilters={setStockOrdersFilters}
            getFiltersList={getStockOrdersFiltersList}
            moduleFiltersTitlesMapping={stockOrdersFilterMapping}
            customGap={48}
            showSort
            showSortInFilter="Ordenar"
            onSortChange={handleSortChange}
            customHeightContainer="customHeightContainer"
            singleSelectFilters={{ sort: true }}
          />
        </div>
      </div>
    </div>
  );
};

export default StockOrdersFilters;
